import { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'types';
import {
  selectClearedFieldRefs,
  selectAlertByIssueId
} from '../store/slice/selectors';
import { highlightField, clearFieldHighlight } from '../utils/fieldHighlighter';
import { mapFieldRefToSelector } from '../utils/fieldMapper';
import {
  resolveAlertScrollWinner,
  PhaseOrderEntry
} from '../utils/alertScrollPriority';
import { useActiveAlertIssues } from './useActiveAlertIssues';
import { actions } from '../store/slice';

/** Return shape of {@link useFieldHighlight} — stable for callers. */
export interface UseFieldHighlightResult {
  clearHighlight: (fieldRef: string | null, ruleId: string) => void;
}

export function useFieldHighlight(
  issueId: string,
  isOpen: boolean,
  phases: PhaseOrderEntry[] = []
): UseFieldHighlightResult {
  const dispatch = useDispatch();
  const clearedFieldRefs = useSelector((state: RootState) =>
    selectClearedFieldRefs(state, issueId)
  );
  const alert = useSelector((state: RootState) =>
    selectAlertByIssueId(state, issueId)
  );
  // Same dismissed+cleared filtering as this hook applies inline below — reused (not
  // reimplemented) so the scroll-priority resolver agrees with every other consumer.
  const activeIssuesForScrollPriority = useActiveAlertIssues(issueId);

  // Track if we've already applied highlights (to disable scrolling on updates)
  const hasAppliedHighlightsRef = useRef<boolean>(false);
  const previousIsOpenRef = useRef<boolean>(false);
  /** Retry timers from the highlight effect — cleared on slideout close and on effect cleanup */
  const highlightRetryTimeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  // Reset flag when slideout closes; clear pending highlight retries so they never run after close
  useEffect(() => {
    if (!isOpen && previousIsOpenRef.current) {
      hasAppliedHighlightsRef.current = false;
      highlightRetryTimeoutsRef.current.forEach(id => clearTimeout(id));
      highlightRetryTimeoutsRef.current = [];
    }
    previousIsOpenRef.current = isOpen;
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !issueId) return;

    // Wait for slideout and features to render fully
    const applyHighlights = () => {
      // Get current active fieldRefs from the alert (if alert exists)
      const currentActiveFieldRefs = new Set<string>();
      if (alert && alert.issues) {
        const activeIssues = alert.issues.filter(
          issue => !alert.dismissedIssueKeys.includes(issue.issueKey)
        );
        activeIssues.forEach(issue => {
          const fieldRefKey = issue.fieldRef || 'null';
          currentActiveFieldRefs.add(fieldRefKey);
        });
      }

      // Find all currently highlighted fields in the DOM for this issueId
      const highlightedElements = document.querySelectorAll(
        `[data-alert-issue-id="${issueId}"].alert-field-highlight`
      );

      // Clear highlights for fields that are no longer in the issues list.
      // Clear directly on the element we have; do not re-query by selector, because
      // some selectors may not match the DOM and would miss the element.
      highlightedElements.forEach(el => {
        const fieldRefAttr = el.getAttribute('data-alert-field-ref');
        const fieldRefKey = fieldRefAttr || 'null';

        if (!currentActiveFieldRefs.has(fieldRefKey)) {
          const element = el as HTMLElement;
          element.classList.remove('alert-field-highlight');
          element.removeAttribute('data-alert-issue-id');
          element.removeAttribute('data-alert-field-ref');
          element.removeAttribute('data-alert-rule-id');
        }
      });

      // Apply highlights for fields that are in the current issues list
      if (alert && alert.issues) {
        const activeIssues = alert.issues.filter(
          issue => !alert.dismissedIssueKeys.includes(issue.issueKey)
        );

        // Determine if we should scroll: only on first highlight (when slideout opens)
        // Disable scrolling on subsequent updates (when alert data refreshes)
        const shouldScroll = !hasAppliedHighlightsRef.current;
        // Only auto-expand sections on first application; on re-apply (e.g. after notification refresh)
        // skip expanding so we don't re-expand General Info / phase headers the user just collapsed
        const skipExpandSection = hasAppliedHighlightsRef.current;

        // Which single issue (across ALL sections — milestones/features included) should get the
        // initial scroll, based on the moment's actual visual section order. Milestones/Features
        // are still never scrolled from THIS imperative path (see mapping?.section check below) —
        // this only decides whether a general-info/overview issue is allowed to win the race, i.e.
        // it stays out of the way when a higher-priority milestone/feature alert exists elsewhere.
        const scrollWinner = resolveAlertScrollWinner(
          activeIssuesForScrollPriority,
          phases
        );
        const scrollWinnerIndex = scrollWinner
          ? activeIssues.findIndex(
              issue => issue.issueKey === scrollWinner.issue.issueKey
            )
          : -1;

        let attemptedImperativeHighlight = false;
        let anyHighlightSucceeded = false;

        activeIssues.forEach((issue, index) => {
          const fieldRefKey = issue.fieldRef || 'null';
          // Only highlight if this fieldRef is NOT in the cleared list
          if (!clearedFieldRefs.includes(fieldRefKey)) {
            const mapping = mapFieldRefToSelector(issue.fieldRef, issue.ruleId);
            // Milestone columns use declarative highlights in MilestoneMatrixFromAPI, and Feature
            // rows use declarative highlights in Features.tsx (both avoid flicker/timing issues
            // on re-render for dynamically-rendered lists — see featureDeclarativeHighlight.ts).
            if (
              mapping?.section === 'milestones' ||
              mapping?.section === 'features'
            ) {
              return;
            }
            attemptedImperativeHighlight = true;
            const ok = highlightField({
              issueId,
              fieldRef: issue.fieldRef,
              ruleId: issue.ruleId,
              // Scroll only on first application (slideout open), and only to the issue that won
              // the visual-order priority contest (see resolveAlertScrollWinner above).
              scrollToField:
                shouldScroll &&
                scrollWinnerIndex >= 0 &&
                index === scrollWinnerIndex,
              skipExpandSection
            });
            if (ok) {
              anyHighlightSucceeded = true;
            }
          }
        });

        // Finish the "first pass" only when there was nothing to paint imperatively (milestones-only / all cleared paths),
        // or when at least one highlightField reported success — so a retry can still scroll/expand if the DOM was not ready on an earlier timer.
        if (!hasAppliedHighlightsRef.current) {
          if (!attemptedImperativeHighlight || anyHighlightSucceeded) {
            hasAppliedHighlightsRef.current = true;
          }
        }
      }
    };

    // First attempt after slideout renders
    const timer1 = setTimeout(applyHighlights, 800);
    // Retry for sections that load asynchronously
    const timer2 = setTimeout(applyHighlights, 2000);
    highlightRetryTimeoutsRef.current = [timer1, timer2];

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      highlightRetryTimeoutsRef.current = [];
    };
  }, [
    isOpen,
    issueId,
    clearedFieldRefs,
    alert,
    activeIssuesForScrollPriority,
    phases
  ]);

  const clearHighlight = (fieldRef: string | null, ruleId: string) => {
    clearFieldHighlight(fieldRef, ruleId);
    dispatch(
      actions.clearFieldHighlight({
        issueId,
        fieldRef: fieldRef || 'null'
      })
    );
  };

  return { clearHighlight };
}
